import { z } from 'zod';

import {
  BlockValueFieldSchemaV2,
  BlockValueFieldSchemaVariant,
} from '../base/blockValueFieldSchema.js';
import { BlockValueNumberSchema } from '../base/blockValueNumberSchema.js';
import { BlockValueStringSchema } from '../base/blockValueStringSchema.js';

export enum ConditionalOperation {
  Equals = 'Equals',
  NotEquals = 'NotEquals',
  InRange = 'InRange',
  NotInRange = 'NotInRange',
  Contains = 'Contains',
  DoesNotContain = 'DoesNotContain',
  StartsWith = 'StartsWith',
  DoesNotStartWith = 'DoesNotStartWith',
  EndsWith = 'EndsWith',
  DoesNotEndWith = 'DoesNotEndWith',
  IsOneOf = 'IsOneOf',
  IsNotOneOf = 'IsNotOneOf',
  GreaterThan = 'GreaterThan',
  GreaterThanOrEqualTo = 'GreaterThanOrEqualTo',
  LessThan = 'LessThan',
  LessThanOrEqualTo = 'LessThanOrEqualTo',
  IsTrue = 'IsTrue',
  IsFalse = 'IsFalse',
  Exists = 'Exists',
  DoesNotExist = 'DoesNotExist',
}

export enum ConditionalFieldDataType {
  String = 'String',
  Number = 'Number',
  Date = 'Date',
  DateTime = 'DateTime',
  Boolean = 'Boolean',
  Value = 'Value',
}

export enum ConditionalCaseSensitivity {
  ExactMatch = 'ExactMatch',
  IgnoreCase = 'IgnoreCase',
}

export enum ConditionalType {
  MatchFirst = 'MatchFirst',
  MatchAll = 'MatchAll',
}

export const CONDITIONAL_ELSE_CONDITION_ID = '__Else__';

const stringWithValueBaseConditionSchema = z.object({
  id: z.string().uuid(),
  label: z.string().optional(),
  inputValue: BlockValueFieldSchemaV2({ variant: BlockValueFieldSchemaVariant.TemplateOnly }),
  dataType: z.literal(ConditionalFieldDataType.String),
  value: BlockValueStringSchema({
    stringValidationSchema: z.string(),
  }),
  caseSensitivity: z
    .nativeEnum(ConditionalCaseSensitivity)
    .default(ConditionalCaseSensitivity.ExactMatch),
});

export const CONDITIONAL_STRING_CONDITION_SCHEMAS = [
  stringWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.Equals),
  }),
  stringWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.NotEquals),
  }),
  stringWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.Contains),
  }),
  stringWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.DoesNotContain),
  }),
  stringWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.IsOneOf),
  }),
  stringWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.IsNotOneOf),
  }),
  stringWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.StartsWith),
  }),
  stringWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.DoesNotStartWith),
  }),
  stringWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.EndsWith),
  }),
  stringWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.DoesNotEndWith),
  }),
] as const;

export const conditionalStringConditionSchema = z.union(CONDITIONAL_STRING_CONDITION_SCHEMAS);

export type ConditionalStringCondition = z.infer<typeof conditionalStringConditionSchema>;

const numberWithValueBaseConditionSchema = z.object({
  id: z.string().uuid(),
  label: z.string().optional(),
  inputValue: BlockValueFieldSchemaV2({ variant: BlockValueFieldSchemaVariant.TemplateOnly }),
  dataType: z.literal(ConditionalFieldDataType.Number),
  value: BlockValueNumberSchema({ numberValidationSchema: z.number() }),
});

const numberWithRangeBaseConditionSchema = z.object({
  id: z.string().uuid(),
  label: z.string().optional(),
  inputValue: BlockValueFieldSchemaV2({ variant: BlockValueFieldSchemaVariant.TemplateOnly }),
  dataType: z.literal(ConditionalFieldDataType.Number),
  minValue: BlockValueNumberSchema({ numberValidationSchema: z.number() }),
  maxValue: BlockValueNumberSchema({ numberValidationSchema: z.number() }),
  minValueInclusive: z.boolean(),
  maxValueInclusive: z.boolean(),
});

export const CONDITIONAL_NUMBER_CONDITION_SCHEMAS = [
  numberWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.Equals),
  }),
  numberWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.NotEquals),
  }),
  numberWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.GreaterThan),
  }),
  numberWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.GreaterThanOrEqualTo),
  }),
  numberWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.LessThan),
  }),
  numberWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.LessThanOrEqualTo),
  }),
  numberWithRangeBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.InRange),
  }),
  numberWithRangeBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.NotInRange),
  }),
] as const;

export const conditionalNumberConditionSchema = z.union(CONDITIONAL_NUMBER_CONDITION_SCHEMAS);

export type ConditionalNumberCondition = z.infer<typeof conditionalNumberConditionSchema>;

const dateWithValueBaseConditionSchema = z.object({
  id: z.string().uuid(),
  label: z.string().optional(),
  inputValue: BlockValueFieldSchemaV2({ variant: BlockValueFieldSchemaVariant.TemplateOnly }),
  dataType: z.literal(ConditionalFieldDataType.Date),
  value: z.union([
    BlockValueFieldSchemaV2({ variant: BlockValueFieldSchemaVariant.TemplateOnly }),
    z.string().datetime(),
    z.string().date(),
  ]),
});

export const CONDITIONAL_DATE_CONDITION_SCHEMAS = [
  dateWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.Equals),
  }),
  dateWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.NotEquals),
  }),
  dateWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.GreaterThan),
  }),
  dateWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.GreaterThanOrEqualTo),
  }),
  dateWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.LessThan),
  }),
  dateWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.LessThanOrEqualTo),
  }),
] as const;

export const conditionalDateConditionSchema = z.union(CONDITIONAL_DATE_CONDITION_SCHEMAS);

export type ConditionalDateCondition = z.infer<typeof conditionalDateConditionSchema>;

const dateTimeWithValueBaseConditionSchema = z.object({
  id: z.string().uuid(),
  label: z.string().optional(),
  inputValue: BlockValueFieldSchemaV2({ variant: BlockValueFieldSchemaVariant.TemplateOnly }),
  dataType: z.literal(ConditionalFieldDataType.DateTime),
  value: z.union([
    BlockValueFieldSchemaV2({ variant: BlockValueFieldSchemaVariant.TemplateOnly }),
    z.string().datetime(),
  ]),
});

export const CONDITIONAL_DATE_TIME_CONDITION_SCHEMAS = [
  dateTimeWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.Equals),
  }),
  dateTimeWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.NotEquals),
  }),
  dateTimeWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.GreaterThan),
  }),
  dateTimeWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.GreaterThanOrEqualTo),
  }),
  dateTimeWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.LessThan),
  }),
  dateTimeWithValueBaseConditionSchema.extend({
    operation: z.literal(ConditionalOperation.LessThanOrEqualTo),
  }),
] as const;

export const conditionalDateTimeConditionSchema = z.union(CONDITIONAL_DATE_TIME_CONDITION_SCHEMAS);

export type ConditionalDateTimeCondition = z.infer<typeof conditionalDateTimeConditionSchema>;

const booleanConditionBaseSchema = z.object({
  id: z.string().uuid(),
  label: z.string().optional(),
  inputValue: BlockValueFieldSchemaV2({ variant: BlockValueFieldSchemaVariant.TemplateOnly }),
  dataType: z.literal(ConditionalFieldDataType.Boolean),
});

export const CONDITIONAL_BOOLEAN_CONDITION_SCHEMAS = [
  booleanConditionBaseSchema.extend({
    operation: z.literal(ConditionalOperation.IsTrue),
  }),
  booleanConditionBaseSchema.extend({
    operation: z.literal(ConditionalOperation.IsFalse),
  }),
] as const;

export const conditionalBooleanConditionSchema = z.union(CONDITIONAL_BOOLEAN_CONDITION_SCHEMAS);

export type ConditionalBooleanCondition = z.infer<typeof conditionalBooleanConditionSchema>;

const valueConditionBaseSchema = z.object({
  id: z.string().uuid(),
  label: z.string().optional(),
  inputValue: BlockValueFieldSchemaV2({ variant: BlockValueFieldSchemaVariant.TemplateOnly }),
  dataType: z.literal(ConditionalFieldDataType.Value),
});

export const CONDITIONAL_VALUE_CONDITION_SCHEMAS = [
  valueConditionBaseSchema.extend({
    operation: z.literal(ConditionalOperation.Exists),
  }),
  valueConditionBaseSchema.extend({
    operation: z.literal(ConditionalOperation.DoesNotExist),
  }),
] as const;

export const conditionalValueConditionSchema = z.union(CONDITIONAL_VALUE_CONDITION_SCHEMAS);

export type ConditionalValueCondition = z.infer<typeof conditionalValueConditionSchema>;

export const ALL_CONDITION_SCHEMAS = [
  ...CONDITIONAL_STRING_CONDITION_SCHEMAS,
  ...CONDITIONAL_NUMBER_CONDITION_SCHEMAS,
  ...CONDITIONAL_DATE_CONDITION_SCHEMAS,
  ...CONDITIONAL_DATE_TIME_CONDITION_SCHEMAS,
  ...CONDITIONAL_BOOLEAN_CONDITION_SCHEMAS,
  ...CONDITIONAL_VALUE_CONDITION_SCHEMAS,
] as const;

const _ConditionSchema = z.union(ALL_CONDITION_SCHEMAS);

export type ConditionalCondition = z.infer<typeof _ConditionSchema>;

const baseConditionSchema = z
  .object({
    id: z.string().uuid(),
    label: z.string().optional(),
    inputValue: BlockValueFieldSchemaV2({ variant: BlockValueFieldSchemaVariant.TemplateOnly }),
    dataType: z.nativeEnum(ConditionalFieldDataType),
    operation: z.nativeEnum(ConditionalOperation),
  })
  .passthrough();

export const conditionalConditionSchema = baseConditionSchema.superRefine(
  (arg, context): arg is ConditionalCondition => {
    for (const schema of ALL_CONDITION_SCHEMAS) {
      if (schema.shape.dataType.value !== arg.dataType) {
        continue;
      }
      if (schema.shape.operation.value !== arg.operation) {
        continue;
      }
      const parseResult = schema.safeParse(arg);
      if (parseResult.success) {
        return z.NEVER;
      }
      parseResult.error.errors.forEach((issue) => {
        context.addIssue(issue);
      });
      return z.NEVER;
    }
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Invalid condition',
    });
    return z.NEVER;
  }
);

export const conditionalConfigSchema = z.object({
  conditionalType: z.nativeEnum(ConditionalType),
  conditions: z.array(conditionalConditionSchema),
});

export type ConditionalConfig = z.infer<typeof conditionalConfigSchema>;
